<template>
  <div class="fee-combo-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('feeComboManage.list.title')" :subtitle="$t('feeComboManage.page.subtitle')">
      <template #extra>
        <el-button type="primary" icon="el-icon-plus" @click="handleAdd">
          {{ $t('common.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.comboName" clearable class="w-full"
            :placeholder="$t('feeComboManage.search.comboName')" @keyup.enter.native="handleSearch" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('feeComboManage.list.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('feeComboManage.page.empty')" @retry="getList">
        <el-table-column prop="comboId" :label="$t('feeComboManage.table.comboId')" align="center" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.comboId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="comboName" :label="$t('feeComboManage.table.comboName')" align="left" min-width="150"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.comboName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('feeComboManage.table.createTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('feeComboManage.table.remark')" align="left" min-width="180"
          show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="ds-text-muted">{{ scope.row.remark || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" fixed="right" width="200">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="handleToComboMember(scope.row)">
              {{ $t('feeComboManage.operation.feeItems') }}
            </el-button>
            <el-button size="mini" type="text" @click="handleEdit(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="handleDelete(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <div class="ds-tip-text">
            <p>{{ $t('feeComboManage.remark.line1') }}</p>
            <p>{{ $t('feeComboManage.remark.line2') }}</p>
          </div>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- 子组件 -->
    <add-fee-combo ref="addFeeCombo" @success="handleSuccess" />
    <edit-fee-combo ref="editFeeCombo" @success="handleSuccess" />
    <delete-fee-combo ref="deleteFeeCombo" @success="handleSuccess" />
  </div>
</template>

<script>
import { listFeeCombo } from '@/api/fee/feeComboManageApi'
import { getCommunityId } from '@/api/community/communityApi'
import AddFeeCombo from '@/components/fee/addFeeCombo'
import EditFeeCombo from '@/components/fee/editFeeCombo'
import DeleteFeeCombo from '@/components/fee/deleteFeeCombo'
import { formatDateTimeSec } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'FeeComboManageList',
  components: {
    AddFeeCombo,
    EditFeeCombo,
    DeleteFeeCombo,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        comboName: ''
      },
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      communityId: ''
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.getList()
  },
  methods: {
    async getList() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.page.current,
          row: this.page.size,
          comboName: this.searchForm.comboName,
          communityId: this.communityId
        }
        const { data, total } = await listFeeCombo(params)
        this.tableData = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('feeComboManage.page.loadFailed')
        this.$message.error(this.$t('feeComboManage.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        comboName: ''
      }
      this.getList()
    },
    handleAdd() {
      this.$refs.addFeeCombo.open()
    },
    handleEdit(row) {
      this.$refs.editFeeCombo.open(row)
    },
    handleDelete(row) {
      this.$refs.deleteFeeCombo.open(row)
    },
    handleToComboMember(row) {
      this.$router.push({
        path: '/views/fee/feeComboMemberManage',
        query: {
          comboId: row.comboId,
          comboName: row.comboName
        }
      })
    },
    handleSuccess() {
      this.getList()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.fee-combo-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;

  p {
    margin: 0;
  }
}

/* 辅助文本 */
.ds-text-muted {
  color: var(--ds-gray-500);
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
